import { uuidv7 } from '../../core/id';
import type { Fixture, Tournament } from '../../schemas/tournament';
import { medianOvr } from './teams';
import type { RatedPlayer } from './types';

/**
 * Round robin, single elimination, standings and advancing a result (R26). All pure. The server
 * stores what the client computed, so fixtures carry their own ids (`newId`, default uuidv7).
 *
 * Mapping to a game: team A of the game is the fixture's entryA, team B is entryB. The
 * lowercase FixtureSide ('a' | 'b') maps to the uppercase Team ('A' | 'B').
 */

/** The two formats a tournament can have. A two-entry knockout looks like a round robin, so callers that know the format pass it. */
export type BracketFormat = Tournament['format'];

export interface BracketOpts {
  newId?: () => string;
}

const blank = (id: string, round: number, slot: number): Fixture => ({
  id,
  round,
  slot,
  entryA: null,
  entryB: null,
  gameId: null,
  nextFixtureId: null,
  nextSide: null,
  status: 'pending',
  scoreA: null,
  scoreB: null,
  winnerEntryId: null,
});

/** Entry ids best first. Unrated entries sit at the median of the rated ones; ties keep input order. */
export function seedByOvr(entries: readonly { id: string; ovr: number | null }[]): string[] {
  const fill = medianOvr(entries.map((e): RatedPlayer => ({ id: e.id, ovr: e.ovr })));
  return entries
    .map((e, i) => ({ id: e.id, v: e.ovr ?? fill, i }))
    .sort((a, b) => b.v - a.v || a.i - b.i)
    .map((e) => e.id);
}

/** Circle method. Every pair meets once (twice with `legs: 2`). An odd field gives one entry a rest per round. */
export function roundRobin(entryIds: readonly string[], opts: BracketOpts & { legs?: 1 | 2 } = {}): Fixture[] {
  const newId = opts.newId ?? uuidv7;
  if (entryIds.length < 2) return [];
  const ring: (string | null)[] = [...entryIds];
  if (ring.length % 2) ring.push(null);
  const m = ring.length;
  const out: Fixture[] = [];
  const legs = opts.legs ?? 1;
  for (let leg = 0; leg < legs; leg++) {
    const arr = [...ring];
    for (let r = 0; r < m - 1; r++) {
      let slot = 0;
      for (let i = 0; i < m / 2; i++) {
        let a = arr[i] ?? null;
        let b = arr[m - 1 - i] ?? null;
        if (a === null || b === null) continue;
        // Berger sides: the fixed entry alternates by round, everybody else by their table row, so
        // nobody is team A much more often than team B. The second leg flips every side.
        const flip = i === 0 ? r % 2 === 1 : i % 2 === 1;
        if (flip !== (leg % 2 === 1)) [a, b] = [b, a];
        out.push({ ...blank(newId(), leg * (m - 1) + r + 1, slot++), entryA: a, entryB: b, status: 'ready' });
      }
      const last = arr.pop() as string | null;
      arr.splice(1, 0, last);
    }
  }
  return out;
}

/** Standard seeding order for a bracket of `size` (power of two): [1, 8, 4, 5, 2, 7, 3, 6] for 8. */
function seedOrder(size: number): number[] {
  let order = [1];
  while (order.length < size) {
    const total = order.length * 2;
    order = order.flatMap((s) => [s, total + 1 - s]);
  }
  return order;
}

function place(fixtures: Fixture[], id: string, side: 'a' | 'b', entryId: string): void {
  const i = fixtures.findIndex((f) => f.id === id);
  const f = fixtures[i];
  if (!f) return;
  const next = side === 'a' ? { ...f, entryA: entryId } : { ...f, entryB: entryId };
  if (next.entryA && next.entryB && next.status === 'pending') next.status = 'ready';
  fixtures[i] = next;
}

/**
 * Single elimination for entries in seed order (index 0 is the top seed). The bracket is padded to
 * a power of two; the top seeds get the byes. A bye fixture is already 'bye' with its winner
 * placed in the next round.
 */
export function singleElim(entryIds: readonly string[], opts: BracketOpts = {}): Fixture[] {
  const newId = opts.newId ?? uuidv7;
  const n = entryIds.length;
  if (n < 2) return [];
  let size = 2;
  while (size < n) size *= 2;
  const rounds = Math.log2(size);
  const order = seedOrder(size);
  const entryOf = (seed: number): string | null => entryIds[seed - 1] ?? null;

  const grid: Fixture[][] = [];
  for (let r = 1; r <= rounds; r++) {
    const count = size / 2 ** r;
    grid.push(Array.from({ length: count }, (_, slot) => blank(newId(), r, slot)));
  }
  for (let r = 0; r < rounds - 1; r++) {
    (grid[r] as Fixture[]).forEach((f, slot) => {
      const nextF = (grid[r + 1] as Fixture[])[slot >> 1] as Fixture;
      f.nextFixtureId = nextF.id;
      f.nextSide = slot % 2 === 0 ? 'a' : 'b';
    });
  }
  const fixtures = grid.flat();
  (grid[0] as Fixture[]).forEach((f, slot) => {
    f.entryA = entryOf(order[slot * 2] as number);
    f.entryB = entryOf(order[slot * 2 + 1] as number);
    if (f.entryA && f.entryB) f.status = 'ready';
    else {
      f.status = 'bye';
      f.winnerEntryId = f.entryA ?? f.entryB;
    }
  });
  for (const f of fixtures) {
    if (f.status === 'bye' && f.nextFixtureId && f.nextSide && f.winnerEntryId) {
      place(fixtures, f.nextFixtureId, f.nextSide, f.winnerEntryId);
    }
  }
  return fixtures;
}

export interface FixtureResult {
  scoreA: number;
  scoreB: number;
}

/**
 * Record a result and move the winner on. Returns a new array. A tie is fine in a round robin
 * (no winner); a knockout fixture (one with a next fixture) needs a winner and throws RangeError
 * otherwise. Changing a result whose winner already played the next round throws Error, because
 * the bracket has moved on (tournaments offers a reset for that).
 */
export function advance(
  fixtures: readonly Fixture[],
  fixtureId: string,
  result: FixtureResult,
  format?: BracketFormat,
): Fixture[] {
  const out = fixtures.map((f) => ({ ...f }));
  const i = out.findIndex((f) => f.id === fixtureId);
  const f = out[i];
  if (!f) throw new RangeError(`no fixture ${fixtureId}`);
  if (!f.entryA || !f.entryB) throw new RangeError('that fixture is not ready yet');
  const { scoreA, scoreB } = result;
  const winner = scoreA > scoreB ? f.entryA : scoreB > scoreA ? f.entryB : null;
  if (!winner && (f.nextFixtureId || format === 'single_elim')) throw new RangeError('a knockout game needs a winner');
  const changed = f.winnerEntryId !== null && f.winnerEntryId !== winner;
  if (changed && f.nextFixtureId) {
    const next = out.find((x) => x.id === f.nextFixtureId);
    if (next && (next.status === 'done' || next.status === 'live')) {
      throw new Error('the winner has already played the next round');
    }
  }
  f.scoreA = scoreA;
  f.scoreB = scoreB;
  f.status = 'done';
  f.winnerEntryId = winner;
  if (winner && f.nextFixtureId && f.nextSide) place(out, f.nextFixtureId, f.nextSide, winner);
  return out;
}

export interface StandingRow {
  entryId: string;
  played: number;
  wins: number;
  losses: number;
  draws: number;
  pf: number;
  pa: number;
  diff: number;
  /** 1-based. Entries level on every tiebreak share a rank (the seed order still sorts them). */
  rank: number;
}

/**
 * Round robin table. Order: wins, then wins in the games among the entries that are level on
 * wins (head-to-head), then point difference, then points for, then input order (the seed).
 * Entries equal on all four keep the same `rank`.
 */
export function standings(entryIds: readonly string[], fixtures: readonly Fixture[]): StandingRow[] {
  const rows = new Map<string, StandingRow>(
    entryIds.map((id) => [
      id,
      { entryId: id, played: 0, wins: 0, losses: 0, draws: 0, pf: 0, pa: 0, diff: 0, rank: 0 },
    ]),
  );
  const beat = new Map<string, number>();
  for (const f of fixtures) {
    if (f.status !== 'done' || !f.entryA || !f.entryB || f.scoreA === null || f.scoreB === null) continue;
    const a = rows.get(f.entryA);
    const b = rows.get(f.entryB);
    if (!a || !b) continue;
    a.played += 1;
    b.played += 1;
    a.pf += f.scoreA;
    a.pa += f.scoreB;
    b.pf += f.scoreB;
    b.pa += f.scoreA;
    if (f.scoreA > f.scoreB) {
      a.wins += 1;
      b.losses += 1;
      beat.set(`${f.entryA}>${f.entryB}`, (beat.get(`${f.entryA}>${f.entryB}`) ?? 0) + 1);
    } else if (f.scoreB > f.scoreA) {
      b.wins += 1;
      a.losses += 1;
      beat.set(`${f.entryB}>${f.entryA}`, (beat.get(`${f.entryB}>${f.entryA}`) ?? 0) + 1);
    } else {
      a.draws += 1;
      b.draws += 1;
    }
  }
  const order = new Map(entryIds.map((id, i) => [id, i]));
  const all = [...rows.values()].map((r) => ({ ...r, diff: r.pf - r.pa }));
  all.sort((x, y) => y.wins - x.wins || (order.get(x.entryId) ?? 0) - (order.get(y.entryId) ?? 0));

  // Walk the runs of equal wins and order each run by its own mini table.
  const out: StandingRow[] = [];
  const mini = new Map<string, number>();
  for (let i = 0; i < all.length; ) {
    let j = i;
    while (j < all.length && (all[j] as StandingRow).wins === (all[i] as StandingRow).wins) j++;
    const run = all.slice(i, j);
    for (const r of run) {
      let w = 0;
      for (const o of run) if (o !== r) w += beat.get(`${r.entryId}>${o.entryId}`) ?? 0;
      mini.set(r.entryId, run.length > 1 ? w : 0);
    }
    run.sort(
      (x, y) =>
        (mini.get(y.entryId) ?? 0) - (mini.get(x.entryId) ?? 0) ||
        y.diff - x.diff ||
        y.pf - x.pf ||
        (order.get(x.entryId) ?? 0) - (order.get(y.entryId) ?? 0),
    );
    out.push(...run);
    i = j;
  }
  const same = (x: StandingRow, y: StandingRow) =>
    x.wins === y.wins &&
    (mini.get(x.entryId) ?? 0) === (mini.get(y.entryId) ?? 0) &&
    x.diff === y.diff &&
    x.pf === y.pf;
  out.forEach((r, i) => {
    const prev = out[i - 1];
    r.rank = prev && same(prev, r) ? prev.rank : i + 1;
  });
  return out;
}

/** Who rests in each round of a round robin with an odd field. Rounds nobody rests are left out. */
export function roundRobinByes(entryIds: readonly string[], fixtures: readonly Fixture[]): Map<number, string> {
  const out = new Map<number, string>();
  if (entryIds.length % 2 === 0) return out;
  const rounds = new Set(fixtures.map((f) => f.round));
  for (const r of rounds) {
    const playing = new Set(fixtures.filter((f) => f.round === r).flatMap((f) => [f.entryA, f.entryB]));
    const rest = entryIds.find((id) => !playing.has(id));
    if (rest) out.set(r, rest);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Recomputing from results (a corrected or deleted game, a changed manual score)
// ---------------------------------------------------------------------------------------------

/** A finished fixture as it was played: which two entries met, and the score. */
export interface RecordedResult {
  fixtureId: string;
  entryA: string;
  entryB: string;
  scoreA: number;
  scoreB: number;
}

/** The results already on the board. */
export function resultsOf(fixtures: readonly Fixture[]): RecordedResult[] {
  const out: RecordedResult[] = [];
  for (const f of fixtures) {
    if (f.status === 'done' && f.entryA && f.entryB && f.scoreA !== null && f.scoreB !== null) {
      out.push({ fixtureId: f.id, entryA: f.entryA, entryB: f.entryB, scoreA: f.scoreA, scoreB: f.scoreB });
    }
  }
  return out;
}

const isKnockout = (fixtures: readonly Fixture[], format?: BracketFormat) =>
  format ? format === 'single_elim' : fixtures.some((f) => f.nextFixtureId !== null);

/**
 * Rebuild the board from the first round and a set of results. A result applies only when the
 * fixture still holds the same two entries it was played between; after a winner changed, the
 * later games between the wrong teams no longer count and come back in `orphaned` (their fixtures
 * are open again). A knockout result that is a tie is orphaned too. Pure: the input is not changed.
 *
 * `gameId` is carried over untouched; whoever stores the games decides what to do with it.
 */
export function rebuildBracket(
  fixtures: readonly Fixture[],
  results: readonly RecordedResult[],
  format?: BracketFormat,
): { fixtures: Fixture[]; orphaned: string[] } {
  const knockout = isKnockout(fixtures, format);
  const firstRound = fixtures.reduce((m, f) => Math.min(m, f.round), Number.POSITIVE_INFINITY);
  const out: Fixture[] = fixtures
    .map((f) => ({ ...f, scoreA: null, scoreB: null, winnerEntryId: null }))
    .sort((a, b) => a.round - b.round || a.slot - b.slot)
    .map((f): Fixture => {
      if (!knockout) return { ...f, status: 'ready' };
      if (f.round > firstRound) return { ...f, entryA: null, entryB: null, status: 'pending' };
      if (f.entryA && f.entryB) return { ...f, status: 'ready' };
      const only = f.entryA ?? f.entryB;
      return { ...f, status: only ? 'bye' : 'pending', winnerEntryId: only };
    });
  for (const f of out) {
    if (f.status === 'bye' && f.nextFixtureId && f.nextSide && f.winnerEntryId) {
      place(out, f.nextFixtureId, f.nextSide, f.winnerEntryId);
    }
  }
  const byFixture = new Map(results.map((r) => [r.fixtureId, r]));
  const orphaned: string[] = [];
  for (let i = 0; i < out.length; i++) {
    const f = out[i] as Fixture;
    const r = byFixture.get(f.id);
    if (!r) continue;
    const tie = r.scoreA === r.scoreB;
    if (f.entryA !== r.entryA || f.entryB !== r.entryB || (tie && knockout)) {
      orphaned.push(f.id);
      continue;
    }
    const winner = r.scoreA > r.scoreB ? r.entryA : r.scoreB > r.scoreA ? r.entryB : null;
    out[i] = { ...f, status: 'done', scoreA: r.scoreA, scoreB: r.scoreB, winnerEntryId: winner };
    if (winner && f.nextFixtureId && f.nextSide) place(out, f.nextFixtureId, f.nextSide, winner);
  }
  return { fixtures: out, orphaned };
}

/** The last fixture of a knockout, or null for a round robin. */
export function finalFixture(fixtures: readonly Fixture[], format?: BracketFormat): Fixture | null {
  if (!isKnockout(fixtures, format)) return null;
  return fixtures.find((f) => f.nextFixtureId === null) ?? null;
}

/** True when every fixture that has to be played has been. */
export function isComplete(fixtures: readonly Fixture[]): boolean {
  return fixtures.length > 0 && fixtures.every((f) => f.status === 'done' || f.status === 'bye');
}

/**
 * The champion once the tournament is complete, else null. A knockout's is the winner of the final.
 * A round robin's is the top of the table; entries level on every tiebreak go by seed order (the
 * order of `entryIds`).
 */
export function championOf(
  entryIds: readonly string[],
  fixtures: readonly Fixture[],
  format?: BracketFormat,
): string | null {
  if (!isComplete(fixtures)) return null;
  const last = finalFixture(fixtures, format);
  if (last) return last.winnerEntryId;
  return standings(entryIds, fixtures)[0]?.entryId ?? null;
}
